import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {RequestStatus} from '../../../../../model/enum/request.status.enum';
import {Router} from '@angular/router';
import {ScreenSizeService} from '../../../../../services/utils/screenSize.service';
import {RequestComponent} from '../../../request/request.component';
import {Partner} from '../../../../models/partner.model';
import {Utils} from '../../../../utils/utils';
import {Quote} from '../../../../models/quote.model';
import {RequestService} from '../../../verify-request/request-service';
import {NotificationsService} from '@uranda/ui';
import {OpenWriteReviewModalService} from '../../../../../services/openWriteReviewModal.service';
import {AmountUtils} from '../../../../../services/utils/format-amounts.utils';
import {ModalOffcanvasService} from '../../../../../services/modal-offcanvas.service';
import {TranslateService} from '@ngx-translate/core';
import {ReviewSidebarComponent} from '../../review-sidebar/review-sidebar.component';
import {OffcanvasHistoryService} from '../../../../../services/offcanvas-history.service';
import {LocalizedRouterService} from '../../../../localized-router.service';
import {ClinicDetailOffcanvasComponent} from '../../clinic-detail/clinic-detail-modal.component';
import {
  ConfirmationModalComponent
} from '../../../../partners/clinic-control-panel/components/confirmation-modal/confirmation-modal.component';
import {Lead} from '../../../../models/lead.model';
import {PdfViewerService} from '../../../../../services/pdf-viewer.service';

@Component({
  selector: 'app-quote-list-item',
  templateUrl: './quote-list-item.component.html',
  styleUrls: ['./quote-list-item.component.scss'],
  standalone: false
})
export class QuoteListItemComponent implements OnInit {
  @ViewChild('confirmationModal') confirmationModal!: ConfirmationModalComponent;
  animationState: 'hidden' | 'visible' = 'hidden';
  @Input() quote?: Quote;
  @Input() token?: string;
  @Input() requestStatus?: RequestStatus;
  @Input() lead?: Lead;
  protected quoteRequested: boolean = false;
  flagsSrcs?: { src: string; name: string }[];
  partner?: Partner;
  flags?: string[] = [];
  AmountUtils = AmountUtils;
  agreePhoneShare: boolean = false;
  @Output() reviewSubmitted = new EventEmitter<void>();
  @Input() hideAmount: boolean = false;
  @Output() quoteInterestConfirmed = new EventEmitter<void>();

  constructor(
    private router: Router,
    protected screenSize: ScreenSizeService,
    private requestService: RequestService,
    private notificationService: NotificationsService,
    private openWriteReviewModalService: OpenWriteReviewModalService,
    private modalOffcanvasService: ModalOffcanvasService,
    private translate: TranslateService,
    private offcanvasHistory: OffcanvasHistoryService,
    private localizedRouter: LocalizedRouterService,
    private pdfViewer: PdfViewerService,
  ) { }

  ngOnInit() {
    setTimeout(() => {
      this.animationState = 'visible';
    }, 100);

    if (this.quote?.partner) {
      this.partner = this.quote.partner;
    }
    if (this.partner?.languagesSpoken) {
      this.flagsSrcs = this.partner?.languagesSpoken.map((code) => ({
        src: Utils.getFlagSrc(code),
        name: Utils.getFlagName(code),
      }));
    }
    this.quoteRequested =
      this.quote?.interestAt != null || this.quote?.interestAt != undefined;
  }

  navigateToRequest() {
    this.modalOffcanvasService.openComponent(RequestComponent, {
      clinicName: this.partner?.name,
      isOffcanvas: !this.screenSize.isDesktop
    });
  }

  sendQuoteRequestDetails() {
    this.agreePhoneShare = false;

    const clinicName = this.partner?.name || 'Clinic';
    const amount = this.quote?.declaredAmount ? AmountUtils.formatAmount(this.quote.declaredAmount, 'EUR') : 'xxxx';

    const includedServices = [
      { show: this.quote?.flightIncluded, label: this.translate.instant('Flight Included'), icon: 'flight_takeoff' },
      { show: this.quote?.accommodationIncluded, label: this.translate.instant('Hotel Included'), icon: 'hotel' },
      { show: this.quote?.isFinanceable, label: this.translate.instant('Financeable'), icon: 'payments' },
      { show: this.quote?.hasTransportToClinic, label: this.translate.instant('Transport to the clinic'), icon: 'swap_driving_apps' }
    ].filter(item => item.show).map(item => item.label);

    const excludedServices = [
      { show: !this.quote?.flightIncluded, label: this.translate.instant('Flight Included') },
      { show: !this.quote?.accommodationIncluded, label: this.translate.instant('Hotel Included') },
      { show: !this.quote?.isFinanceable, label: this.translate.instant('Financeable') },
      { show: !this.quote?.hasTransportToClinic, label: this.translate.instant('Transport to the clinic') }
    ].filter(item => item.show).map(item => item.label);

    const modalRef = this.confirmationModal.open({
      title: this.translate.instant('Request detailed quote'),
      message: this.translate.instant('You are requesting the full quote from {{clinicName}} based on this summary.', { clinicName }),
      submessage: this.translate.instant('Indicative amount: {{amount}} — included and excluded services.', { amount }),
      confirmText: this.translate.instant('Send Request'),
      cancelText: this.translate.instant('Cancel'),
      confirmButtonClass: 'btn-primary',
      size: 'lg',
      showPhoneShareCheckbox: true,
      phoneShareMessage: this.translate.instant('Your phone number will be shared only if the request is accepted, to allow contact and delivery of the detailed quote.'),
      checkboxText: this.translate.instant('I confirm that I want to send this request and authorize the sharing of my phone number.'),
      includedServices: includedServices,
      excludedServices: excludedServices
    });

    if (!modalRef) return;

    modalRef.result
      .then(async () => {
        if (this.confirmationModal.phoneShareAccepted) {
          await this.confirmQuoteInterest();
          this.quoteInterestConfirmed.emit();
        }
      })
      .catch(() => { });
  }

  async confirmQuoteInterest() {
    try {
      if (this.token && this.quote?.id) {
        this.quoteRequested = await this.requestService.quoteInterest(
          this.token,
          this.quote.id
        );
      }
    } catch (error) {
      console.error(error);
      this.notificationService.error(this.translate.instant('Something went wrong'));
    }
  }

  get hasReviews(): boolean {
    return (this.partner?.reviewsCount ?? 0) > 0;
  }

  get reviewsLabel(): string {
    const count = this.partner?.reviewsCount ?? 0;
    const review = count === 1 ? 'review' : 'reviews';
    return `${count} ${this.translate.instant(review)}`;
  }

  async openReviewsOffcanvas(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    try {

      const offcanvasRef = this.offcanvasHistory.open(
        ReviewSidebarComponent,
        {
          position: 'end',
          scroll: false,
          backdrop: true,
          panelClass: 'reviews-offcanvas-panel',
        }
      );

      offcanvasRef.componentInstance.partnerId = this.partner?.id;
      offcanvasRef.componentInstance.clinicName = this.partner?.name;
      offcanvasRef.componentInstance.averageRating = this.partner?.averageRate;
      offcanvasRef.componentInstance.totalReviews = this.partner?.reviewsCount;

      offcanvasRef.result.then(() => {
        document.body.style.overflow = 'auto';
      })

    } catch (error) { }
  }

  async navigateToReview() {
    if (this.partner?.id && this.partner?.name) {

      const result = await this.openWriteReviewModalService.openWriteReviewModalWithCustomer({
        id: this.partner.id,
        name: this.partner.name,
      },
        {
          name: this.lead?.firstname ??'' ,
          countryCode: this.lead?.countryCode ?? '',
          phoneNumber: this.lead?.phoneNumber ?? '',
          leadAssignmentId: this.quote?.leadAssignmentId
        },
        false,
        undefined, true);
      if (result) {
        this.reviewSubmitted.emit();
      }
    } else {
      console.error('Partner information not available for review');
      this.notificationService.error('Unable to open review form');
    }
  }

  protected readonly RequestStatus = RequestStatus;

  async navigateToDetail() {
    if (!this.partner?.id) return;

    const lang = this.localizedRouter.getCurrentLang();
    const segments = [lang, 'partner', this.partner.id];

    if (this.screenSize.isMobile) {
      this.openDetailsModal(this.partner);
    } else {
      const url = this.router.serializeUrl(this.router.createUrlTree(segments));
      window.open(url, '_blank');
    }
  }

  openDetailsModal(partner: Partner) {
    const modalRef = this.offcanvasHistory.open(ClinicDetailOffcanvasComponent, {
      position: 'end',
      scroll: false,
      backdrop: 'static',
      panelClass: 'clinic-details-offcanvas-panel',
    });
    modalRef.componentInstance.partner = partner;
    modalRef.componentInstance.isOffcanvas = true;
    modalRef.result.then(() => {
      document.body.style.overflow = 'auto';
    });
  }

  viewPdf() {
    const pdfUrl = this.quote?.pdf;

    if (!pdfUrl) {
      console.warn('No PDF url available');
      this.notificationService.error(this.translate.instant('PDF not available'));
      return;
    }

    const clinicName = this.partner?.name || '';
    this.pdfViewer.open({
      title: clinicName
        ? `${this.translate.instant('Quote')} - ${clinicName}`
        : this.translate.instant('Quote'),
      source: { url: pdfUrl },
      loadingLabel: this.translate.instant('Loading Quote...'),
      errorLabel: this.translate.instant('Error opening PDF'),
    });
  }

}
